import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import type { AssignedMachineResponse, UserRole } from '@bridgewerk/shared';
import { getIdTokenPayload } from '../../lib/amplify.js';
import { getApiClient } from '../../lib/api-client.js';

export interface AuthUser {
  readonly sub: string;
  readonly email?: string;
  readonly shopId: string;
}

export interface AuthState {
  readonly user: AuthUser | null;
  readonly shopId: string | null;
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  /** Role from DynamoDB. null while loading or no record exists (treated as manager). */
  readonly role: UserRole | null;
  /** Convenience flags derived from role (null → manager for backward compat). */
  readonly isManager: boolean;
  readonly isProgrammer: boolean;
  readonly isOperator: boolean;
  /** null = manager (sees all machines). Set = operator/programmer's assigned machine IDs. */
  readonly assignedMachineIds: ReadonlySet<string> | null;
  readonly displayName: string | null;
  readonly signOut: () => Promise<void>;
  readonly refresh: () => Promise<void>;
}

interface ProfileState {
  role: UserRole;
  displayName: string | null;
  assignedMachineIds: ReadonlySet<string> | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<ProfileState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mounted = useRef(true);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [current, payload] = await Promise.all([
        getCurrentUser().catch(() => null),
        getIdTokenPayload(),
      ]);
      if (!mounted.current) return;
      if (!current || !payload) {
        setUser(null);
        setProfile(null);
        return;
      }
      const shopId = payload['custom:shop_id'];
      if (typeof shopId !== 'string' || shopId.length === 0) {
        setUser(null);
        setProfile(null);
        return;
      }
      const email = typeof payload['email'] === 'string' ? (payload['email'] as string) : undefined;
      const nextUser: AuthUser = { sub: current.userId, email, shopId };
      setUser(nextUser);

      // Fetch role + assignments from the API (best-effort; fall back to manager default)
      try {
        const me = await getApiClient().getMe(shopId);
        if (!mounted.current) return;
        const assignedMachines: AssignedMachineResponse[] = me.assigned_machines ?? [];
        const machineIds = assignedMachines.map(a => a.machine_id);
        setProfile({
          role: me.role,
          displayName: me.display_name,
          assignedMachineIds: me.role === 'manager' ? null : new Set(machineIds),
        });
      } catch {
        if (!mounted.current) return;
        // No profile record or network error — default to manager for backward compat
        setProfile({ role: 'manager', displayName: null, assignedMachineIds: null });
      }
    } catch {
      if (!mounted.current) return;
      setUser(null);
      setProfile(null);
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    const stop = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          void load();
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          if (mounted.current) { setUser(null); setProfile(null); }
          break;
      }
    });
    return () => {
      mounted.current = false;
      stop();
    };
  }, [load]);

  const signOut = useCallback(async (): Promise<void> => {
    await amplifySignOut();
    setUser(null);
    setProfile(null);
  }, []);

  const value = useMemo<AuthState>(() => {
    const effectiveRole: UserRole = profile?.role ?? 'manager';
    return {
      user,
      shopId: user?.shopId ?? null,
      isLoading,
      isAuthenticated: user != null,
      role: profile?.role ?? null,
      isManager: effectiveRole === 'manager',
      isProgrammer: effectiveRole === 'programmer',
      isOperator: effectiveRole === 'operator',
      assignedMachineIds: profile?.assignedMachineIds ?? null,
      displayName: profile?.displayName ?? null,
      signOut,
      refresh: load,
    };
  }, [user, profile, isLoading, signOut, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
